import Header from '../components/Header';
import Footer from '../components/Footer';
import WholesaleForm from '../components/WholesaleForm';
import SEO from '../components/SEO';
import { useState } from 'react';

const Wholesale = () => {
  const [showZoom, setShowZoom] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setZoomPosition({ x, y });
  };

  return (
    <div className="min-h-screen">
      <SEO 
        title="Wholesale Biltong & White Label"
        description="Premium wholesale biltong for retailers, cafes, gyms, and distributors. Custom white-label packaging available. Bulk pricing, flexible MOQ, Australia-wide delivery."
        keywords="wholesale biltong, bulk biltong, white label biltong, biltong supplier Australia, biltong distributor, private label beef snacks, wholesale protein snacks"
        url="/wholesale"
      />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-20 pb-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-900 to-gray-800 text-white">
        <div className="max-w-7xl mx-auto text-center">
          <div className="mb-6 text-sm tracking-[0.3em] text-gray-400 font-semibold">
            WHOLESALE & WHITE LABELING
          </div>
          <h1 className="text-5xl md:text-7xl font-bold mb-6">
            BUILD YOUR<br />
            <span className="text-red-500">BILTONG EMPIRE</span>
          </h1>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Partner with Original Bull to bring authentic 200-year-old biltong traditions to your market. We support diverse meat traditions and have all necessary licenses for global expansion.
          </p>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mt-10 max-w-4xl mx-auto">
            <div>
              <div className="text-4xl font-bold text-red-500 mb-2">50+</div>
              <div className="text-gray-400">Countries Served</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-red-500 mb-2">200</div>
              <div className="text-gray-400">Years Heritage</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-red-500 mb-2">24/7</div>
              <div className="text-gray-400">Support</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-red-500 mb-2">100%</div>
              <div className="text-gray-400">Quality Guaranteed</div>
            </div>
          </div>
        </div>
      </section>

      {/* Premium Packaging Section - UPDATED with New Package Design Only */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">PREMIUM PACKAGING</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Professional packaging solutions for retail and white-label partnerships
            </p>
          </div>

          {/* Single Packaging Layout - New Design */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-gray-50 rounded-2xl overflow-hidden shadow-xl">
              <div className="p-8 md:p-12">
                <h3 className="text-3xl font-bold mb-8 text-center">ORIGINAL BILTONG PACKAGING</h3>
                
                {/* Package Images */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
                  <div className="space-y-4 relative">
                    <div 
                      className="relative overflow-hidden rounded-lg shadow-lg cursor-zoom-in"
                      onMouseEnter={() => setShowZoom(true)}
                      onMouseLeave={() => setShowZoom(false)}
                      onMouseMove={handleMouseMove}
                    >
                      <img 
                        src="https://customer-assets.emergentagent.com/job_f284c399-6648-4c61-afe7-7584ae6da493/artifacts/7no57v05_New%20Package%20Design.jpg" 
                        alt="Biltong Package Front and Back" 
                        className="w-full rounded-lg shadow-lg"
                        data-testid="new-packaging-image"
                      />
                      
                      {/* Magnifying Glass Overlay */}
                      {showZoom && (
                        <div 
                          className="absolute w-64 h-64 border-4 border-white rounded-full pointer-events-none shadow-2xl overflow-hidden"
                          style={{
                            left: `${zoomPosition.x}%`,
                            top: `${zoomPosition.y}%`,
                            transform: 'translate(-50%, -50%)',
                            backgroundImage: `url('https://customer-assets.emergentagent.com/job_f284c399-6648-4c61-afe7-7584ae6da493/artifacts/7no57v05_New%20Package%20Design.jpg')`,
                            backgroundSize: '400%',
                            backgroundPosition: `${zoomPosition.x}% ${zoomPosition.y}%`,
                            zIndex: 10
                          }}
                        />
                      )}
                    </div>
                    <p className="text-center text-gray-600 font-medium">
                      Front & Nutritional Information
                      <span className="block text-sm text-gray-500 mt-1">🔍 Hover to zoom and read ingredients</span>
                    </p>
                  </div>
                  
                  <div className="flex flex-col justify-center space-y-6">
                    <div className="bg-white p-6 rounded-lg shadow-sm">
                      <h4 className="font-bold text-lg mb-3 text-red-700">Package Features</h4>
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex items-center">
                          <span className="text-green-600 mr-2">✓</span>
                          50G Protein per serving
                        </li>
                        <li className="flex items-center">
                          <span className="text-green-600 mr-2">✓</span>
                          100% Australian Beef
                        </li>
                        <li className="flex items-center">
                          <span className="text-green-600 mr-2">✓</span>
                          Premium CoQ10 label
                        </li>
                        <li className="flex items-center">
                          <span className="text-green-600 mr-2">✓</span>
                          Complete nutritional information
                        </li>
                        <li className="flex items-center">
                          <span className="text-green-600 mr-2">✓</span>
                          Original Bull branding
                        </li>
                      </ul>
                    </div>

                    <div className="bg-white p-6 rounded-lg shadow-sm">
                      <h4 className="font-bold text-lg mb-3 text-red-700">Available Sizes</h4>
                      <div className="flex flex-wrap gap-3">
                        <span className="px-4 py-2 bg-gray-100 rounded-full text-sm font-medium">25g</span>
                        <span className="px-4 py-2 bg-red-100 text-red-700 rounded-full text-sm font-medium">50g</span>
                        <span className="px-4 py-2 bg-gray-100 rounded-full text-sm font-medium">100g</span>
                        <span className="px-4 py-2 bg-gray-100 rounded-full text-sm font-medium">Bulk</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* White Label Options */}
                <div className="bg-white rounded-xl p-8 border-2 border-gray-200">
                  <h4 className="text-2xl font-bold mb-6 text-center">WHITE LABEL PACKAGING OPTIONS</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="text-center p-6 bg-gray-50 rounded-lg">
                      <div className="text-3xl mb-3">🎨</div>
                      <h5 className="font-bold mb-2">Custom Branding</h5>
                      <p className="text-sm text-gray-600">Your logo and brand colours on premium packaging</p>
                    </div>
                    <div className="text-center p-6 bg-gray-50 rounded-lg">
                      <div className="text-3xl mb-3">📦</div>
                      <h5 className="font-bold mb-2">Multiple Sizes</h5>
                      <p className="text-sm text-gray-600">25g, 50g, 100g, and bulk options available</p>
                    </div>
                    <div className="text-center p-6 bg-gray-50 rounded-lg">
                      <div className="text-3xl mb-3">✅</div>
                      <h5 className="font-bold mb-2">Compliance Ready</h5>
                      <p className="text-sm text-gray-600">Meets all local labeling and certification requirements</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Business Opportunities */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">BUSINESS OPPORTUNITIES</h2>
            <p className="text-gray-600">Choose the partnership model that fits your market and ambitions</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: 'FOOD SERVICE DISTRIBUTION',
                subtitle: 'Restaurants, cafes, hotels',
                volume: '500+ kg/month',
              },
              {
                title: 'RETAIL CHAIN PARTNERSHIP',
                subtitle: 'Supermarkets, specialty stores',
                volume: '1000+ kg/month',
              },
              {
                title: 'WHITE LABEL MANUFACTURING',
                subtitle: 'Your brand, our expertise',
                volume: '2000+ kg/month',
              },
              {
                title: 'REGIONAL FRANCHISE OPPORTUNITY',
                subtitle: 'Exclusive territory rights',
                volume: '5000+ kg/month',
              },
            ].map((opportunity, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-lg hover:shadow-2xl transition-shadow">
                <h3 className="font-bold text-lg mb-2">{opportunity.title}</h3>
                <p className="text-gray-600 text-sm mb-4">{opportunity.subtitle}</p>
                <div className="pt-4 border-t border-gray-200">
                  <div className="text-sm text-gray-500 mb-1">Minimum Volume</div>
                  <div className="text-xl font-bold text-red-700">{opportunity.volume}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Respecting Meat Traditions */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">RESPECTING MEAT TRADITIONS</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Original Bull holds comprehensive certifications and licenses to support diverse cultural and religious meat traditions across all markets.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 mb-12">
            {[
              { icon: '🕌', title: 'HALAL CERTIFIED', desc: 'Middle East, Asia, Africa' },
              { icon: '🌱', title: 'GRASS-FED PREMIUM', desc: 'Europe, North America' },
              { icon: '🐄', title: 'FREE-RANGE PREMIUM', desc: 'Australia, New Zealand' },
              { icon: '👑', title: 'WAGYU PREMIUM', desc: 'Japan, Premium Markets' },
              { icon: '🏛️', title: 'LOCAL HERITAGE BREEDS', desc: 'Regional Specialties' },
              { icon: '⭐', title: 'PREMIUM ARTISAN', desc: 'Global Markets' },
            ].map((cert, index) => (
              <div key={index} className="text-center p-6 bg-gray-50 rounded-xl">
                <div className="text-4xl mb-3">{cert.icon}</div>
                <h3 className="font-bold mb-2 text-sm">{cert.title}</h3>
                <p className="text-xs text-gray-600">{cert.desc}</p>
              </div>
            ))}
          </div>

          <div className="bg-gray-900 text-white rounded-2xl p-8 md:p-12">
            <h3 className="text-2xl font-bold mb-6 text-center">OUR CERTIFICATIONS</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {['HACCP Certified', 'Halal Certified', 'Australian Made', 'Premium Quality'].map((cert, index) => (
                <div key={index} className="text-center p-6 bg-gray-800 rounded-lg">
                  <div className="text-3xl text-red-500 mb-2">✓</div>
                  <div className="font-medium">{cert}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Partnership Form */}
      <section id="partnership-form" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">START YOUR PARTNERSHIP</h2>
            <p className="text-gray-600">
              Complete the form below and our wholesale team will contact you within 24 hours
            </p>
          </div>

          <WholesaleForm />
        </div>
      </section>

      {/* Payment Options */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">PAYMENT & ORDERING</h2>
            <p className="text-gray-600">
              Flexible payment options and streamlined ordering process for all partnership levels
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: 'STRIPE PAYMENTS',
                items: ['B2B invoicing & payments', '135+ currencies supported', 'Wholesale volume discounts', 'Subscription billing available'],
              },
              {
                title: 'PAYPAL',
                items: ['Trusted global payments', '200+ countries supported', 'Express checkout', 'Buyer protection included'],
              },
              {
                title: 'BANK TRANSFER',
                items: ['Direct bank transfers', 'Large volume orders', 'Net payment terms', 'Australian banking support'],
              },
              {
                title: 'FLEXIBLE PAYMENT TERMS',
                items: ['NET 30 - Established partners', 'COD - Cash on delivery', 'INSTANT - Online processing', ''],
              },
            ].map((payment, index) => (
              <div key={index} className="bg-gray-50 p-8 rounded-xl">
                <h3 className="font-bold text-lg mb-4">{payment.title}</h3>
                <ul className="space-y-2">
                  {payment.items.map((item, i) => (
                    item && (
                      <li key={i} className="text-sm text-gray-700 flex items-start">
                        <span className="text-red-700 mr-2">•</span>
                        {item}
                      </li>
                    )
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Partner */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">WHY PARTNER WITH ORIGINAL BULL</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-8">
              <div className="text-5xl mb-4">🏆</div>
              <h3 className="text-2xl font-bold mb-4">200 YEARS EXPERTISE</h3>
              <p className="text-gray-400">
                Time-tested South African recipes and traditional methods perfected over two centuries, now crafted with premium Australian beef.
              </p>
            </div>
            <div className="text-center p-8">
              <div className="text-5xl mb-4">🌍</div>
              <h3 className="text-2xl font-bold mb-4">GLOBAL REACH</h3>
              <p className="text-gray-400">
                Comprehensive certifications enabling distribution across diverse markets and meat traditions.
              </p>
            </div>
            <div className="text-center p-8">
              <div className="text-5xl mb-4">📈</div>
              <h3 className="text-2xl font-bold mb-4">RAPID SCALING</h3>
              <p className="text-gray-400">
                Proven systems and support infrastructure to help you scale quickly in your market.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Wholesale;
